// Matches a plain URL beginning with `http://`, `https://`, or `www.`.
const URL_TOKEN_REGEX = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/;

// Triggers a rescan when nodes matching these selectors are inserted.
// Anything else (tooltips, focus rings, popups, Kendo decorators) is ignored.
const RESCAN_TRIGGER_SELECTOR = ".isReadonlyField, .k-table-td, .k-editor-inline, input[readonly], input[disabled]";

// Interactive Kendo widgets — inputs inside these must not be linkified or
// the widget's DOM will be destroyed. A bare `.k-input` (Kendo TextBox wrapper)
// is intentionally NOT in this list: disabled/readonly TextBoxes are exactly
// what the user wants linkified. `.k-editor` is also intentionally absent —
// a read-only inline editor is handled below via `linkifyContainer`.
const KENDO_INTERACTIVE_WIDGET_SELECTOR = ".k-combobox, .k-dropdown, .k-dropdownlist, .k-datepicker, .k-datetimepicker, .k-timepicker, .k-multiselect, .k-numerictextbox, .k-autocomplete";

// Selectors that mark an element as edit-capable; skip the whole container.
const INTERACTIVE_CHILD_SELECTOR = "a, input, select, textarea, [contenteditable=\"true\"]";

// Container selectors scanned for plain-text URLs.
const READONLY_CONTAINER_SELECTOR = ".isReadonlyField, .k-table-td, .k-editor-inline";

function ensureProtocol(url: string): string {
    if (/^https?:\/\//i.test(url)) {
        return url;
    }
    return `https://${url}`;
}

function buildAnchor(href: string, text: string): HTMLAnchorElement {
    const a = document.createElement("a");
    a.href = href;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.className = "readOnlyLink";
    a.textContent = text;
    return a;
}

function linkifyText(text: string): { fragment: DocumentFragment; matched: boolean } {
    const fragment = document.createDocumentFragment();
    let matched = false;
    let lastIndex = 0;
    URL_TOKEN_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = URL_TOKEN_REGEX.exec(text)) !== null) {
        let raw = match[0];
        let trailing = "";
        const trailMatch = TRAILING_PUNCTUATION.exec(raw);
        if (trailMatch) {
            trailing = trailMatch[0];
            raw = raw.substring(0, raw.length - trailing.length);
        }
        if (!raw) {
            continue;
        }

        if (match.index > lastIndex) {
            fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
        }
        fragment.appendChild(buildAnchor(ensureProtocol(raw), raw));
        if (trailing) {
            fragment.appendChild(document.createTextNode(trailing));
        }
        lastIndex = match.index + raw.length + trailing.length;
        matched = true;
    }
    if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
    }
    return { fragment, matched };
}

function isInsideInteractiveKendoWidget(el: Element): boolean {
    return el.closest(KENDO_INTERACTIVE_WIDGET_SELECTOR) !== null;
}

function linkifyReadOnlyInput(input: HTMLInputElement): void {
    if (isInsideInteractiveKendoWidget(input)) {
        return;
    }
    const value = input.value;
    if (!value) {
        return;
    }
    const result = linkifyText(value);
    if (!result.matched) {
        return;
    }
    // Inside a Kendo `.k-input` wrapper keep the inner styling so the layout
    // doesn't collapse; otherwise use the existing read-only field class.
    const kInputWrapper = input.closest(".k-input");
    const span = document.createElement("span");
    span.className = kInputWrapper ? "readOnlyLinkContainer" : "isReadonlyField";
    span.appendChild(result.fragment);
    input.replaceWith(span);
    // `.k-disabled` on the wrapper sets `pointer-events: none` on all
    // descendants, which blocks the anchor's click. Strip it once we have
    // replaced the input with our own clickable link.
    if (kInputWrapper) {
        kInputWrapper.classList.remove("k-disabled");
    }
}

function linkifyContainer(el: HTMLElement): void {
    if (isInsideInteractiveKendoWidget(el)) {
        return;
    }
    // The element itself may be the editable surface (e.g. a Kendo inline
    // editor in edit mode is a `contenteditable="true"` div).
    if (el.getAttribute("contenteditable") === "true") {
        return;
    }
    // Skip if the container already has a link or hosts an editor control
    // (e.g. a Kendo grid edit-cell).
    if (el.querySelector(INTERACTIVE_CHILD_SELECTOR)) {
        return;
    }
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
        textNodes.push(node as Text);
    }
    for (const tn of textNodes) {
        const value = tn.nodeValue ?? "";
        if (!value) {
            continue;
        }
        const result = linkifyText(value);
        if (result.matched) {
            tn.parentNode?.replaceChild(result.fragment, tn);
        }
    }
}

export function applyReadOnlyLinkify(root: ParentNode = document): void {
    const inputs = root.querySelectorAll<HTMLInputElement>(
        'input[type="text"][readonly], input:not([type])[readonly], input[type="text"][disabled], input:not([type])[disabled]'
    );
    inputs.forEach((input) => {
        try {
            linkifyReadOnlyInput(input);
        } catch {
            // Defensive: never break page rendering due to linkify failure.
        }
    });
    const fields = root.querySelectorAll<HTMLElement>(READONLY_CONTAINER_SELECTOR);
    fields.forEach((el) => {
        try {
            linkifyContainer(el);
        } catch {
            // Defensive: never break page rendering due to linkify failure.
        }
    });
}

// Expose for manual triggering after dynamic content changes (e.g. Kendo grid
// dataBound handlers).
(window as unknown as { applyReadOnlyLinkify: typeof applyReadOnlyLinkify }).applyReadOnlyLinkify = applyReadOnlyLinkify;

let observer: MutationObserver | null = null;

function runOnce(): void {
    try {
        applyReadOnlyLinkify();
    } catch {
        // Defensive: never break page rendering.
    }
    // Drop mutations our own DOM changes just queued — they would only
    // schedule a redundant no-op rescan.
    observer?.takeRecords();
}

let rescanScheduled = false;
function scheduleRescan(): void {
    if (rescanScheduled) {
        return;
    }
    rescanScheduled = true;
    // Coalesce burst mutations (e.g. a Kendo grid binding 100 rows at once)
    // into a single linkify pass on the next frame.
    requestAnimationFrame(() => {
        rescanScheduled = false;
        runOnce();
    });
}

function containsRelevantNode(nodes: NodeList): boolean {
    for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        if (node.nodeType !== Node.ELEMENT_NODE) {
            continue;
        }
        const el = node as Element;
        if (el.matches(RESCAN_TRIGGER_SELECTOR) || el.querySelector(RESCAN_TRIGGER_SELECTOR) !== null) {
            return true;
        }
    }
    return false;
}

function observeLateRenders(): void {
    if (typeof MutationObserver === "undefined" || !document.body) {
        return;
    }
    observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
            if (m.addedNodes.length > 0 && containsRelevantNode(m.addedNodes)) {
                scheduleRescan();
                return;
            }
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });
}

function schedule(): void {
    // setTimeout(0) re-queues after any synchronous `$(document).ready` /
    // Kendo widget initialisation that runs inside the DOMContentLoaded turn.
    setTimeout(runOnce, 0);
    // window.load is a safety net for widgets initialised by late-loading
    // module scripts.
    window.addEventListener("load", runOnce, { once: true });
    // Kendo grids bind data asynchronously, so cells appear after `load`.
    // A MutationObserver covers anything injected later.
    observeLateRenders();
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedule);
} else {
    schedule();
}
